// @flow

import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { filter, propOr, toPairs, or } from 'ramda'
import { isTruthy } from 'ramda-adjunct'

import { VERSION, GIT_REVISION } from '@alphaott/smart-tv-config'
import {
  getManufacturer,
  getDeviceModel,
  getOs,
  getOsVersion,
  getDeviceId,
  getNetworkInfo,
} from '@alphaott/smart-tv-current-device/selectors'
import { getCustomer } from '@alphaott/app-core/selectors'

import { getDeviceType } from '@alphaott/smart-tv-current-device/models/utils'

import { usePublicIP } from '@alphaott/smart-tv-platforms'

export const useDeviceInfo = () => {
  const { t } = useTranslation()

  const publicIpAddress = usePublicIP()

  const appVersion = useMemo(() => `${VERSION}-${GIT_REVISION}`, [])
  const bootloaderVersion = useMemo(() => propOr('', 'bootloaderVersion', window), [])

  const manufacturer = useSelector(getManufacturer)
  const deviceModel = useSelector(getDeviceModel)
  const os = useSelector(getOs)
  const osVersion = useSelector(getOsVersion)
  const deviceId = useSelector(getDeviceId)
  const networkInfo = useSelector(getNetworkInfo)
  const customer = useSelector(getCustomer)

  const fullName = useMemo(
    () =>
      ['firstName', 'middleName', 'lastName']
        .map((k) => (customer?.[k] ?? '').trim())
        .filter(Boolean)
        .join(' '),
    [customer],
  )

  // Todo: Добавить Email & Id;
  const deviceInfo = useMemo(
    // eslint-disable-next-line complexity
    () => ({
      [t('mwstv_support_device_type')]: getDeviceType(),
      [t('mwstv_support_device_brand')]: manufacturer,
      [t('mwstv_support_device_model')]: deviceModel,
      [t('mwstv_support_device_platform')]: or(os, osVersion) && `${os} - ${osVersion}`,
      [t('mwstv_support_device_udid')]: deviceId,
      [t('mwstv_support_device_public_ip')]: publicIpAddress,
      [t('mwstv_support_device_local_ip')]: networkInfo?.localIpAddress,

      ...(networkInfo?.mode === 'WIFI' && {
        [t('mwstv_support_wifi_mac')]: networkInfo?.macAddress,
      }),

      ...(networkInfo?.mode === 'ETHERNET' && {
        [t('mwstv_support_ethernet_mac')]: networkInfo?.macAddress,
      }),

      [t('mwstv_support_app_version')]: `${bootloaderVersion} - (${appVersion})`,
      [t('mwstv_name')]: fullName,
      [t('mwstv_support_email_address')]: customer?.email,
      // eslint-disable-next-line no-underscore-dangle
      [t('mwstv_support_user_id')]: customer?._id,
    }),
    [
      t,
      manufacturer,
      deviceModel,
      os,
      osVersion,
      deviceId,
      publicIpAddress,
      networkInfo?.localIpAddress,
      networkInfo?.mode,
      networkInfo?.macAddress,
      bootloaderVersion,
      appVersion,
      fullName,
      customer,
    ],
  )

  const preparedDeviceInfo = useMemo(() => toPairs(filter(isTruthy, deviceInfo)), [deviceInfo])

  return preparedDeviceInfo
}
